import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, Alert, Row, Col, Form } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twitchApi, twitchSettingsApi, botApi } from '../api/api';

const PERMISSION_OPTIONS = [
  { value: 'everyone', label: 'Alle' },
  { value: 'followers', label: 'Follower' },
  { value: 'subscribers', label: 'Abonnenten' },
  { value: 'vip', label: 'VIP' },
  { value: 'moderators', label: 'Moderatoren' },
];

const ACCOUNT_LABELS = { main: 'Haupt-Konto', bot: 'Bot-Konto' };

export default function TwitchSettings() {
  const [status, setStatus] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [channelPoints, setChannelPoints] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingBot, setLoadingBot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const connected = searchParams.get('connected');
  const type = searchParams.get('type');

  useEffect(() => {
    twitchApi.status().then((r) => setStatus(r.data)).catch(() => {});
    botApi.status().then((r) => setBotStatus(r.data)).catch(() => {});
    twitchSettingsApi.get().then((r) => setSettings(r.data)).catch(() => {});
    twitchApi.channelPoints().then((r) => setChannelPoints(r.data?.rewards || [])).catch(() => {});
  }, []);

  const handleConnect = (t) => {
    if (t === 'main') setLoadingMain(true);
    else setLoadingBot(true);
    twitchApi.connect(t);
  };

  const handleDisconnect = async (t) => {
    await twitchApi.disconnect(t);
    twitchApi.status().then((r) => setStatus(r.data)).catch(() => {});
  };

  const chat = status?.chat;
  const chatInfo = (() => {
    if (!chat) return null;
    if (chat.sender === 'user_bot') {
      return { variant: 'info', text: <>Der Bot schreibt im Chat als <strong>{chat.sender_name}</strong> (dein eigener Bot-Account).</> };
    }
    if (chat.sender === 'system_bot') {
      return { variant: 'info', text: <>Der Bot schreibt im Chat als <strong>{chat.sender_name}</strong> (zentraler Bot). Du musst nichts weiter einrichten.</> };
    }
    if (chat.sender === 'main') {
      return { variant: 'info', text: <>Der Bot schreibt im Chat als dein Haupt-Konto <strong>{chat.sender_name}</strong>.</> };
    }
    return { variant: 'warning', text: 'Es ist kein nutzbares Chat-Konto vorhanden. Bitte verbinde dein Haupt-Konto (neu).' };
  })();

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      const { data } = await twitchSettingsApi.update(payload);
      setSettings(data);
      toast.success('Twitch-Einstellungen gespeichert!');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const AccountCard = ({ accountType, label, emoji }) => {
    const info = status?.[accountType];
    return (
      <Card bg="dark" text="white" className="border-secondary h-100">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>{emoji} Twitch {label}</Card.Title>
            <Badge bg={info?.connected ? 'success' : 'secondary'}>
              {info?.connected ? 'Verbunden' : 'Nicht verbunden'}
            </Badge>
          </div>
          {info?.connected ? (
            <>
              <p className="text-muted mb-2">
                Konto: <strong className="text-white">{info.display_name}</strong>
                <span className="text-muted ms-2">({info.channel_name})</span>
              </p>
              <Button variant="outline-danger" size="sm" onClick={() => handleDisconnect(accountType)}>
                Trennen
              </Button>
            </>
          ) : (
            <>
              {accountType === 'bot' && (
                <div
                  className="d-flex align-items-start gap-2 rounded p-2 mb-3"
                  style={{ background: 'rgba(13,110,253,0.1)', border: '1px solid rgba(13,110,253,0.3)' }}
                >
                  <span style={{ fontSize: '1rem', lineHeight: 1.4 }}>ℹ️</span>
                  <span className="text-muted small">
                    <strong className="text-white">Optional</strong> - Standardmäßig schreibt der zentrale Bot
                    {chat?.sender === 'system_bot' ? <> (<strong className="text-white">{chat.sender_name}</strong>)</> : ''} im Chat.
                    Nur wenn du lieber einen eigenen Bot-Account verwenden möchtest, verbinde ihn hier.
                  </span>
                </div>
              )}
              <p className="text-muted small mb-3">
                {accountType === 'main'
                  ? 'Dein Haupt-Twitch-Konto. Wird für Channel-Point-Rewards verwendet und erlaubt dem Bot, in deinem Chat zu schreiben.'
                  : 'Eigener Bot-Account für Chat-Nachrichten (z.B. ein dedizierter Bot-Username).'}
              </p>
              <Button
                variant="outline-primary" size="sm"
                onClick={() => handleConnect(accountType)}
                disabled={accountType === 'main' ? loadingMain : loadingBot}
              >
                {(accountType === 'main' ? loadingMain : loadingBot)
                  ? 'Weiterleitung…'
                  : `🟣 ${label}-Konto verbinden`}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      <h4 className="text-white mb-4">🟣 Twitch-Einstellungen</h4>

      {connected && (
        <Alert variant="success" dismissible>
          Twitch {type === 'bot' ? 'Bot-Konto' : 'Haupt-Konto'} erfolgreich verbunden!
        </Alert>
      )}

      {chat?.reconnect?.length > 0 && (
        <Alert variant="warning">
          Bitte verbinde dein {chat.reconnect.map((k) => ACCOUNT_LABELS[k] || k).join(' und dein ')} neu.
          Der Bot braucht neue Twitch-Berechtigungen, damit er weiterhin im Chat schreiben kann.
        </Alert>
      )}

      {chatInfo && <Alert variant={chatInfo.variant}>{chatInfo.text}</Alert>}

      {chat?.sender && chat.sender !== 'main' && !chat.channel_bot_granted && (
        <Alert variant="secondary">
          Damit der Bot in deinem Chat schreiben darf, verbinde dein Haupt-Konto neu (Berechtigung „Bot im Chat erlauben")
          oder mache den Bot zum Moderator (<code>/mod {chat.sender_name}</code>).
        </Alert>
      )}

      {botStatus?.issue === 'chat_subscription_failed' && (
        <Alert variant="danger">
          Der Bot konnte den Chat deines Channels nicht abonnieren. Prüfe, ob der Bot Moderator ist oder dein
          Haupt-Konto mit der Berechtigung „Bot im Chat erlauben" verbunden ist.
        </Alert>
      )}

      {botStatus?.issue === 'reward_subscription_failed' && (
        <Alert variant="danger">
          Der Bot konnte die Channel-Point-Einlösungen nicht abonnieren. Verbinde dein Haupt-Konto neu und prüfe die
          ausgewählte Belohnung.
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={6}>
          <AccountCard accountType="main" label="Haupt-Konto" emoji="👤" />
        </Col>
        <Col md={6}>
          <AccountCard accountType="bot" label="Bot-Konto" emoji="🤖" />
        </Col>
      </Row>

      {settings && (
        <Card bg="dark" text="white" className="border-secondary">
          <Card.Body>
            <Card.Title className="mb-3">Channel-Einstellungen</Card.Title>
            <p className="text-muted small mb-3">
              Diese Einstellungen gelten nur für deinen Channel.
            </p>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Check type="switch" label="Nur wenn Stream live (Song Requests)"
                  checked={settings.only_when_live}
                  onChange={(e) => update({ only_when_live: e.target.checked })} className="mb-2" />
                <Form.Check type="switch" label="Moderator-Freigabe für Song Requests"
                  checked={settings.moderator_approve_sr}
                  onChange={(e) => update({ moderator_approve_sr: e.target.checked })} className="mb-2" />
                <Form.Check type="switch" label="Channel-Point-Reward auto-erfüllen"
                  checked={settings.auto_fulfill_reward}
                  onChange={(e) => update({ auto_fulfill_reward: e.target.checked })} />
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small">Queue-Anzeige im Chat (Einträge)</Form.Label>
                  <Form.Control type="number" min={1} max={10} size="sm"
                    value={settings.queue_display_count}
                    onChange={(e) => update({ queue_display_count: +e.target.value })}
                    className="bg-dark text-white border-secondary" />
                </Form.Group>

                <Form.Group>
                  <Form.Label className="small">Channel-Point Reward für Song Requests</Form.Label>
                  <Form.Select size="sm"
                    value={settings.sr_channel_points_reward_id || ''}
                    onChange={(e) => update({ sr_channel_points_reward_id: e.target.value || null })}
                    className="bg-dark text-white border-secondary">
                    <option value="">Deaktiviert</option>
                    {channelPoints.map((r) => (
                      <option key={r.id} value={r.id}>{r.title} ({r.cost} Punkte)</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Song Requests erlaubt für</Form.Label>
                  <Form.Select size="sm"
                    value={settings.sr_allowed_for}
                    onChange={(e) => update({ sr_allowed_for: e.target.value })}
                    className="bg-dark text-white border-secondary">
                    {PERMISSION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">VoteSkip erlaubt für</Form.Label>
                  <Form.Select size="sm"
                    value={settings.voteskip_allowed_for}
                    onChange={(e) => update({ voteskip_allowed_for: e.target.value })}
                    className="bg-dark text-white border-secondary">
                    {PERMISSION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Button variant="success" onClick={saveSettings} disabled={saving}>
              {saving ? 'Speichere…' : '💾 Einstellungen speichern'}
            </Button>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
